import { useState, useEffect, useMemo } from "react";
import classes from "./PaketAndGroupSize.module.css"
import { useBooking } from "../../../store/bookingStore";
import { resolvePackagePriceEntry } from "../../../util/resolvePackagePriceEntry";

export const PaketAndGroupSize = ({ bookingStudioData, data, nextSlide }) => {
  const { bookingData, updateBookingData } = useBooking();
  
  // Get packages from either data or bookingStudioData
  const packages = useMemo(
    () => data?.packages || bookingStudioData?.packages || [],
    [data?.packages, bookingStudioData?.packages]
  );
  const isPackagePricing = Array.isArray(packages) && packages.length > 0;
  
  // Normalize packages - sort by duration
  const normalizedPackages = useMemo(() => {
    if (!isPackagePricing) return [];
    return [...packages].sort((a, b) => {
      const aHours = a?.durationHours ?? 0;
      const bHours = b?.durationHours ?? 0;
      return aHours - bHours;
    });
  }, [isPackagePricing, packages]);

  // Get unique person groups across all packages
  const personGroups = useMemo(() => {
    if (!isPackagePricing) return [];
    const map = new Map();
    normalizedPackages.forEach((pkg) => {
      if (!pkg?.prices || !Array.isArray(pkg.prices)) return;
      pkg.prices.forEach((p) => {
        if (!p) return;
        const min = typeof p.minPersons === 'number' ? p.minPersons : parseInt(p.minPersons || '0', 10);
        const max = typeof p.maxPersons === 'number' ? p.maxPersons : parseInt(p.maxPersons || '0', 10);
        if (!min || !max) return;
        const key = `${min}-${max}`;
        if (!map.has(key)) {
          map.set(key, { min, max, key });
        }
      });
    });
    const groups = Array.from(map.values());
    groups.sort((a, b) => a.min - b.min);
    return groups;
  }, [isPackagePricing, normalizedPackages]);

  // Initialize with lowest package and lowest people group
  const [selectedPackageId, setSelectedPackageId] = useState(() => {
    return bookingData?.packageId || (normalizedPackages.length > 0 ? normalizedPackages[0]?.id : null);
  });
  
  const [selectedPeopleGroupKey, setSelectedPeopleGroupKey] = useState(() => {
    return bookingData?.selectedPeopleGroupKey || (personGroups.length > 0 ? personGroups[0]?.key : null);
  });

  // Update booking data when selections change
  useEffect(() => {
    if (selectedPackageId) {
      updateBookingData('packageId', selectedPackageId);
      const selectedPkg = normalizedPackages.find(p => p.id === selectedPackageId);
      if (selectedPkg) {
        updateBookingData('packageName', selectedPkg.name || `${selectedPkg.durationHours} ${selectedPkg.durationHours === 1 ? 'Stunde' : 'Stunden'}`);
        updateBookingData('packageDurationHours', selectedPkg.durationHours);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackageId, normalizedPackages]);

  useEffect(() => {
    if (selectedPeopleGroupKey) {
      updateBookingData('selectedPeopleGroupKey', selectedPeopleGroupKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeopleGroupKey]);

  // Get selected package
  const getSelectedPackage = () => {
    if (!isPackagePricing || normalizedPackages.length === 0) return null;
    const found = normalizedPackages.find((pkg) => pkg.id === selectedPackageId);
    return found || normalizedPackages[0];
  };

  // Get price for a specific package (using lowest people group price for display)
  const getPackagePrice = (pkg) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return 0;
    // Get the lowest price (first price entry, which should be for smallest group)
    const firstPrice = pkg.prices[0];
    const rawPrice = firstPrice?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) ? num : 0;
  };

  // Get base price for lowest people group for a specific package (to calculate additional cost)
  const getLowestGroupBasePrice = (pkg) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return 0;
    
    // Get the first (lowest) price entry
    const firstPrice = pkg.prices[0];
    const rawPrice = firstPrice?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) ? num : 0;
  };

  const getPackagePriceEntryForGroup = (pkg, groupKey) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0 || !groupKey) {
      return null;
    }
    return resolvePackagePriceEntry(pkg.prices, groupKey);
  };

  // Calculate additional cost for a people group with a specific package
  const getAdditionalCostForGroup = (group, pkg) => {
    if (!pkg || !Array.isArray(pkg.prices)) return 0;

    const priceEntry = resolvePackagePriceEntry(pkg.prices, group.key);

    if (!priceEntry) return 0;
    
    const groupPrice = typeof priceEntry.price === 'number' ? priceEntry.price : parseFloat(priceEntry.price || '0');
    const lowestPrice = getLowestGroupBasePrice(pkg);
    
    return Math.max(0, groupPrice - lowestPrice);
  };

  // Calculate total price (for selected package + people group) and the
  // additional people-based surcharge compared to the lowest group.
  const {
    totalPrice,
    packagePeoplePrice,
    packageBasePrice,
    pricingUnavailable,
    pricingUnavailableReason,
  } = useMemo(() => {
    const pkg = normalizedPackages.find((pkg) => pkg.id === selectedPackageId) || normalizedPackages[0];
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) {
      return {
        totalPrice: 0,
        packagePeoplePrice: 0,
        packageBasePrice: 0,
        pricingUnavailable: true,
        pricingUnavailableReason: 'Für dieses Paket ist kein Preis hinterlegt.',
      };
    }

    // Get the base price (lowest people group price for this package)
    const lowestBase = getLowestGroupBasePrice(pkg);

    const priceEntry = selectedPeopleGroupKey
      ? getPackagePriceEntryForGroup(pkg, selectedPeopleGroupKey)
      : pkg.prices[0];

    if (!priceEntry) {
      return {
        totalPrice: 0,
        packagePeoplePrice: 0,
        packageBasePrice: lowestBase,
        pricingUnavailable: true,
        pricingUnavailableReason: 'Preis für diese Personengruppe nicht verfügbar.',
      };
    }

    const rawPrice = priceEntry?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    if (!Number.isFinite(num) || num <= 0) {
      return {
        totalPrice: 0,
        packagePeoplePrice: 0,
        packageBasePrice: lowestBase,
        pricingUnavailable: true,
        pricingUnavailableReason: 'Ungültiger Paketpreis für diese Personengruppe.',
      };
    }

    const groupPrice = num;

    const peopleExtra = Math.max(0, groupPrice - lowestBase);

    return {
      totalPrice: groupPrice,
      packagePeoplePrice: peopleExtra,
      packageBasePrice: lowestBase,
      pricingUnavailable: false,
      pricingUnavailableReason: null,
    };
  }, [selectedPackageId, selectedPeopleGroupKey, normalizedPackages]);

  // Persist the stable studio/package price in booking data so later steps
  // (like TimePickerPacket and BookingOverview) can display the same value.
  // Also persist the explicit people surcharge and base package price.
  useEffect(() => {
    updateBookingData('packagePeoplePrice', pricingUnavailable ? 0 : packagePeoplePrice);
    updateBookingData('packageBasePrice', pricingUnavailable ? 0 : packageBasePrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagePeoplePrice, packageBasePrice, pricingUnavailable]);

  // Check if both are selected (if no person groups, only package selection is needed)
  const isBothSelected = selectedPackageId && (personGroups.length === 0 || selectedPeopleGroupKey);
  const canContinue = Boolean(isBothSelected && !pricingUnavailable);

  const handlePackageSelect = (packageId) => {
    setSelectedPackageId(packageId);
  };

  const handlePeopleGroupSelect = (groupKey) => {
    setSelectedPeopleGroupKey(groupKey);
  };

  const handleContinue = () => {
    if (canContinue) {
      updateBookingData('packagePeoplePrice', packagePeoplePrice);
      updateBookingData('packageBasePrice', packageBasePrice);
      nextSlide();
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.packagesBox}>
        <h3 className={`${classes.boxTitle} semiboldMiddle`}>Paket auswählen</h3>
        <div className={classes.optionsContainer}>
          {normalizedPackages.map((pkg) => {
            const isSelected = selectedPackageId === pkg.id;
            const packagePrice = getPackagePrice(pkg);
            return (
              <div
                key={pkg.id}
                className={`${classes.option} ${isSelected ? classes.selected : ''}`}
                onClick={() => handlePackageSelect(pkg.id)}
              >
                <span className="regularLarge">{pkg.durationHours} {pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}</span>
                <div className={classes.priceBadge}>
                  <span className="semiboldMiddle">{packagePrice}€</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={classes.peopleBox}>
        <h3 className={`${classes.boxTitle} semiboldMiddle`}>Personenanzahl wählen</h3>
        <div className={classes.optionsContainer}>
          {personGroups.map((group) => {
            const isSelected = selectedPeopleGroupKey === group.key;
            const pkg = getSelectedPackage();
            const additional = pkg ? getAdditionalCostForGroup(group, pkg) : 0;
            
            return (
              <div
                key={group.key}
                className={`${classes.option} ${isSelected ? classes.selected : ''}`}
                onClick={() => handlePeopleGroupSelect(group.key)}
              >
                <span className="regularLarge">
                  {group.min === group.max 
                    ? `${group.min} ${group.min === 1 ? 'Person' : 'Personen'}`
                    : `${group.min} - ${group.max} ${group.max === 1 ? 'Person' : 'Personen'}`
                  }
                </span>
                {additional > 0 && (
                  <div className={classes.priceBadge}>
                    <span className="semiboldMiddle">+ {additional}€</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.totalPrice}>
          <span className="semiboldSmall">Gesamtbetrag</span>
          <span style={{color: "var(--dark-grey-3)"}} className="regularSmall">
            {pricingUnavailable ? 'Nicht verfügbar' : `${totalPrice}€`}
          </span>
        </div>
        {pricingUnavailable && (
          <p className="regularSmall" style={{ color: 'var(--dark-grey-3)', margin: 0 }}>
            {pricingUnavailableReason}
          </p>
        )}
        <button
          className={`${classes.continueButton} regularLarge ${!canContinue ? classes.disabled : ''}`}
          onClick={handleContinue}
          disabled={!canContinue}
        >
          Termin finden
        </button>
      </div>
    </div>
  );
};

export default PaketAndGroupSize;
